// File sizes, scaled like the mobile `ByteCountFormatter` with a `.file`
// count style (1000-based units). Screens show them through useFormatBytes,
// which writes the number and the unit in the page language.

export type ByteUnit = 'kb' | 'mb' | 'gb' | 'tb';

const UNITS: ByteUnit[] = ['kb', 'mb', 'gb', 'tb'];

export const scaleBytes = (
  bytes: number,
): { amount: number; unit: ByteUnit } => {
  const safe = Math.max(bytes, 0);

  // Below 1 KB we still report in KB (matching mobile), rounding up sub-KB
  // sizes that aren't exactly zero.
  if (safe < 1000) {
    return {
      amount: safe === 0 ? 0 : Math.max(1, Math.round(safe / 1000)),
      unit: 'kb',
    };
  }

  let value = safe / 1000;
  let unitIndex = 0;
  while (value >= 1000 && unitIndex < UNITS.length - 1) {
    value /= 1000;
    unitIndex += 1;
  }

  // One decimal for fractional values under 100.
  const amount = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;

  return { amount, unit: UNITS[unitIndex] };
};

// Which badge a file gets, worked out from its name's extension. Mirrors the
// Mac app's `MacFileTypeBadge.Format`: PDF and Word documents and Markdown
// have their own colour and short label; anything else shows its extension.
export type FileBadgeFormat = {
  // Short label printed on the badge, like "PDF" or "DOC".
  label: string;
  // Fixed, saturated colour so it reads the same in light and dark mode.
  color: string;
};

export const fileBadgeFormat = (fileName?: string | null): FileBadgeFormat => {
  const extension = fileName?.includes('.')
    ? fileName.split('.').pop()!.toLowerCase()
    : 'pdf';

  switch (extension) {
    case 'pdf':
      return { label: 'PDF', color: '#d4362a' };
    case 'doc':
    case 'docx':
      return { label: 'DOC', color: '#2b5799' };
    case 'md':
    case 'markdown':
      return { label: 'MD', color: '#38404d' };
    default:
      return {
        label: extension ? extension.slice(0, 4).toUpperCase() : '?',
        color: '#737373',
      };
  }
};
